import * as d3 from 'd3'
import utils from './index'
import displaySettings from '../assets/config/chart_display_settings.json'
import { getUnitLabel } from './unit'

import { $i18n, $i18nMessages } from './localization'

import { ChartLegends } from './charts/ChartLegends'

/**
 * Class used to handle chart properties and data
 */
export class ChartView {
  /**
   * constructor
   * @param {*} dataset
   * @param {*} chartType
   * @param {*} size {width: val, height: val}
   * @param {*} margin {top: val, right: val, bottom: val, left: val}
   */
  constructor(chartId, dataset, chartType, size, margin) {
    this.chartId = chartId
    this.margin = margin
      ? margin
      : {
          top: 20,
          right: 20,
          bottom: 30,
          left: 50,
        }
    this.$t = $i18n
    this.$i18n = $i18nMessages
    this.width = size.width
    this.height = size.height
    this.radius = 250
    this.svgWidth = size.width
    this.svgHeight = size.height
    this.svg = Object()
    this.g = Object()
    this.tooltip = Object()
    this.xAxis = Object()
    this.yAxis = Object()
    this.xAxisScale = null
    this.yAxisScale = null
    this.dataset = dataset
    this.chartType = chartType
    this.sortedDataset = {}
    this.otherCategories = []
    this.chartLegends = new ChartLegends([], [], this, {})
    this.mode = 'normal'
    this.labelTextSize = '6px'
  }

  /**
   * Draw the current chart
   */
  draw() {}

  /**
   * Set used dataset for chart
   * @param {*} dataset
   */
  setDataset(dataset) {
    this.dataset = dataset
  }

  /**
   * Init SVG container use to display the current chart
   * @param {*} chartId
   */
  initContainer(chartId) {
    this.svgWidth = this.width + this.margin.left + this.margin.right * 2
    /*this.svgHeight = this.chartType == 'bar' || this.chartType == 'pie' ? this.height + this.margin.top * 2 + this.margin.bottom * 6 :
              sortBy && sortBy != 'all' ? this.height + this.margin.top * 2 + this.margin.bottom * 4 : this.height + this.margin.top * 2 + this.margin.bottom;*/

    this.svgHeight = this.height + this.margin.top + this.margin.bottom * 2

    this.svg = d3
      .select('#' + chartId)
      .append('svg')
      .attr('id', 'svg-' + chartId)
      .attr('width', '100%')
      .attr('viewBox', '0 0 ' + this.svgWidth + ' ' + this.svgHeight)
      .attr('preserveAspectRatio', 'xMaxYMin')
      .append('g')
      .attr('transform', 'translate(' + this.margin.left + ',' + this.margin.top * 2 + ')')
  }

  /**
   * Init the tooltip used to display mouse over informations on charts
   * @param {*} otherCategories : categories in the "others" group for small values
   */
  initTooltip(otherCategories) {
    let that = this

    d3.selectAll('.d3-tip').style('opacity', 0)

    this.otherCategories = otherCategories

    this.tooltip = d3
      .tip()
      .attr('class', 'd3-tip')
      .html(function(d) {
        if (that.config.id) {
          return that.getTooltipText(d)
        } else {
          //For chart's used in survey's tab
          if (d.data || d.count) {
            let item = d.data ? d.data : d
            if (item.category == 'others') {
              const columnNumber = Math.floor(that.otherCategories.length / 10)
              let htmlTooltip = `<ul style="columns: ${columnNumber}; -webkit-columns: ${columnNumber}; -moz-columns: ${columnNumber};">`

              that.otherCategories.forEach(item => {
                if (item.category != 'others') {
                  htmlTooltip +=
                    '<li>' +
                    that.getSurveyLegendText(item.category, item.count, item.percent) +
                    '</li>'
                }
              })

              htmlTooltip += '</ul>'
              return htmlTooltip
            } else {
              return that.getSurveyLegendText(item.category, item.count, item.percent)
            }
          } else {
            if (String(d).startsWith(that.$t('others'))) {
              const columnNumber = Math.floor(that.otherCategories.length / 10)
              let htmlTooltip = `<ul style="columns: ${columnNumber}; -webkit-columns: ${columnNumber}; -moz-columns: ${columnNumber};">`
              that.otherCategories.forEach(item => {
                if (item.category != 'others') {
                  htmlTooltip +=
                    '<li>' +
                    that.getSurveyLegendText(item.category, item.count, item.percent) +
                    '</li>'
                }
              })
              return htmlTooltip
            } else {
              return d
            }
          }
        }
      })

    this.tooltip.hide
    this.tooltip.offset([-5, 0])
    if (this.svg && this.tooltip && this.svg.call(this.tooltip)) {
      this.svg.call(this.tooltip).style('margin-bottom: 20px')
    }
  }

  /**
   * Generate tooltip text according to chart's type
   * @param {*} category
   * @param {*} value
   * @param {*} percent
   */
  getTooltipText(category, value, percent) {
    let tooltipText = ''

    let sortBy =
      this.chartType === 'bar' || this.chartType === 'pie'
        ? this.dataset.distributionType
        : this.dataset.sortBy
    if (this.chartType === 'time' || this.chartType === 'bar' || this.chartType === 'pie') {
      if (category === 'all' || category === 'total' || category === 'others') {
        tooltipText = this.$t('filter.' + category)
      } else if (
        sortBy === 'site_id' ||
        sortBy === 'language' ||
        sortBy === 'product' ||
        sortBy === 'warning_type'
      ) {
        tooltipText += this.$t(`${sortBy}.${category}`)
      } else if (sortBy === 'site' || sortBy === 'site_location') {
        tooltipText = this.$i18n()[sortBy][category]
      } else if (sortBy === 'histopad_rating' || sortBy === 'visit_rating') {
        tooltipText +=
          category + ' ' + `${Number(category) > 0 ? this.$t('stars') : this.$t('star')}`
      } else if (sortBy === 'collected_treasure_count') {
        tooltipText +=
          category + ' ' + `${Number(category) > 0 ? this.$t('treasures') : this.$t('treasures')}`
      } else {
        tooltipText += category
      }
      if (this.chartType == 'bar' || this.chartType == 'pie') {
        if (this.dataset.dataType === 'count' || this.dataset.dataType === 'distribution') {
          tooltipText += ' - ' + value + ' (' + percent + '%)'
        } else {
          tooltipText += ` - ${value} ${
            this.dataset.unit !== 'no' ? getUnitLabel(this.dataset) : ''
          }`
        }
      } else {
        if (value >= 0) {
          tooltipText += ' : ' + value
        }
      }
    } else if (this.chartType === 'day') {
      tooltipText = this.$t(category)
    } else if (this.chartType === 'matrix') {
      if (category.time) {
        tooltipText =
          category.date.format('DD/MM/YYYY') +
          ' - ' +
          category.time +
          '</br>' +
          this.$t('affluence') +
          ' : ' +
          category.affluence +
          '<br/>' +
          this.$t('start') +
          ' : ' +
          category.start +
          '<br/>' +
          this.$t('end') +
          ' : ' +
          category.end
      } else {
        tooltipText = this.$t('affluence') + ' : ' + category
      }
    }

    return tooltipText
  }

  /**
   * Generate legend's text according to chart's type
   * @param {*} category
   */
  getLegendText(category) {
    let legendText = category
    if (this.chartType == 'time' || this.chartType == 'bar' || this.chartType == 'pie') {
      let sortBy =
        this.chartType == 'bar' || this.chartType == 'pie'
          ? this.dataset.distributionType
          : this.dataset.sortBy

      if (category === 'all' || category === 'total' || category === 'others') {
        legendText = this.$t('filter.' + category)
      } else if (
        sortBy === 'site_id' ||
        sortBy === 'language' ||
        sortBy === 'product' ||
        sortBy === 'warning_type'
      ) {
        legendText = this.$t(`${sortBy}.${category}`)
      } else if (sortBy === 'site' || sortBy === 'site_location') {
        legendText = this.$i18n()[sortBy][category]
      } else if (sortBy === 'histopad_rating' || sortBy == 'visit_rating') {
        legendText = category + ' ' + `${Number(category) > 0 ? this.$t('stars') : this.$t('star')}`
      } else if (sortBy === 'histopad_rating' || sortBy == 'visit_rating') {
        legendText = category + ' ' + `${Number(category) > 0 ? this.$t('stars') : this.$t('star')}`
      } else if (sortBy === 'collected_treasure_count') {
        legendText =
          category + ' ' + `${Number(category) > 0 ? this.$t('treasures') : this.$t('treasures')}`
      } else if (
        sortBy == 'duration' ||
        sortBy == 'version' ||
        sortBy == 'player_version' ||
        sortBy == 'os_version' ||
        sortBy == 'os_build' ||
        sortBy == 'os_name' ||
        sortBy == 'device_vendor' ||
        sortBy == 'device_model' ||
        sortBy == 'bucket'
      ) {
        legendText = category
      } else {
        legendText = this.$t('filter.all')
      }
    } else if (this.chartType == 'day') {
      legendText = this.$t(category)
    } else if (this.chartType == 'matrix') {
      legendText = category
    } else {
      if (this.dataset.sortBy == 'all') {
        legendText = this.$t('filter.all')
      }
    }
    return legendText
  }

  /**
   * Display grid if needed
   * @param {*} charts
   */
  displayGrid() {
    let opacity =
      this.config.display_options && this.config.display_options.display_grid == true ? 0.2 : 0
    this.svg.selectAll('.tick').each(function() {
      var el = d3.select(this).select('.tick-line')
      el.style('opacity', opacity)
    })
  }

  /**
   * Set checked legends
   * @param {*} checked
   */
  setChecked(checked) {
    this.checked = checked
  }

  /**
   * Set unchecked legends
   * @param {*} unchecked
   */
  setUnchecked(unchecked) {
    this.unchecked = unchecked
  }

  /**
   * Set checked legends
   * @param {*} checked
   */
  setGridCharts(gridCharts) {
    this.gridCharts = gridCharts
  }

  /**
   * Set sorted dataset
   * @param {*} sortedDataset
   */
  setSortedDataset(sortedDataset) {
    this.sortedDataset = sortedDataset
  }

  /**
   * Set chart's titles
   * @param {*} title
   * @param {*} xTitle
   * @param {*} yTitle
   */
  setTitles(title, xTitle, yTitle) {
    this.title = title
    this.xTitle = xTitle
    this.yTitle = yTitle
  }

  /**
   * Set chart's config
   * @param {*} config
   */
  setConfig(config) {
    this.config = config

    this.width = displaySettings[this.chartType].width
    this.height =
      this.config.display_options && this.config.display_options.display_mode == 'compact'
        ? displaySettings[this.chartType].height / 2
        : displaySettings[this.chartType].height

    //Update colors palette according to the chart's config
    switch (this.config.type) {
      case 'pie':
      case 'bar':
        this.colors = utils.colorSet(this.dataset.distributionType)
        break
      case 'time':
        if (this.config.sort_by) {
          this.colors = utils.colorSet(this.config.sort_by)
        }
        break
      case 'day':
        this.colors = {
          start: '#63E2FF',
          end: '#f68084',
          affluence: '#00B7FF',
        }
        break
      case 'rotation':
        this.colors = ['#ffffff', '#ffffd9', '#c7e9b4', '#41b6c4', '#253494']
        break
      default:
        this.colors = utils.colorsDomain()
        break
    }
  }

  /**
   * Update zoomed charts
   * @param {*} charts
   */
  setZoomCharts(charts) {
    this.zoomCharts = charts
  }

  /**
   * Set chart's current size
   * @param {*} size
   */
  setChartSize(size) {
    this.height = size.height
    this.width = size.width
  }

  /**INTERFACES**/

  /**
   * Create current chart (redraw it)
   */
  createChart() {}

  /**
   * Display new legends for current chart
   */
  createLegends(legends, legendColors) {
    if (this.config.display_options && this.config.display_options.legend == 'none') {
      return
    }

    this.chartLegends.updateConfig(legends, legendColors, this, this.legendOptions)

    this.chartLegends.draw()
  }

  /**
   * Return legend opacity according to his checked status
   * @param {*} d
   * @param {*} i
   */
  // eslint-disable-next-line no-unused-vars
  getLegendOpacity(d, i) {}

  /**
   * Return if chart is expanded
   */
  isExpandedChart() {
    return (
      (this.config.display_options && this.config.display_options.display_mode == 'expanded') ||
      !this.config.display_options
    )
  }

  /**
   * Return legend check status
   * @param {*} d
   * @param {*} i
   */
  // eslint-disable-next-line no-unused-vars
  isLegendChecked(d, i) {}

  /**
   * Legend click handler
   * @param {*} clickedLegend
   * @param {*} d
   */
  // eslint-disable-next-line no-unused-vars
  clickOnLegend(clickedLegend, d, i) {}

  /**
   * Return Y axis max value according to chart's type and his display options
   */
  getYMaxValue() {
    let maxValue = 0
    if (this.chartType == 'time') {
      if (this.stackedData.length > 0) {
        maxValue = d3.max(this.stackedData, function(stack) {
          let max = d3.max(stack, function(d) {
            return d[1]
          })
          return max
        })
      } else {
        maxValue = d3.max(this.dataset.items, function(item) {
          return item.value
        })
      }
    } else if (this.chartType == 'day') {
      maxValue = d3.max(this.sortedDataset.lines, function(line) {
        return d3.max(line.items, function(item) {
          return item.value
        })
      })
    } else if (this.chartType == 'bar') {
      maxValue = d3.max(this.sortedDataset.items, function(d) {
        // return d.percent
        return d.count
      })
    }

    //Adding 10% add the top of the chart to have some space to display the on over tooltips
    maxValue = maxValue + (maxValue * 10) / 100

    return maxValue
  }

  /**
   * Set displaying mode "preview" or "normal"
   * @param {*} mode
   */
  setMode(mode) {
    this.mode = mode
  }
}
